import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const ROLE_LABEL = { passenger: "Passager", driver: "Chauffeur" };

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const initialRole = search.get("role") === "driver" ? "driver" : "passenger";

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: initialRole,
    vehicle_type: "standard",
    vehicle_plate: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Compte créé !");
      if (u.role === "driver") navigate("/driver");
      else navigate("/passenger");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Échec de l'inscription");
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex bg-[#FFD600] flex-col justify-between p-12 border-r-2 border-black">
        <Link to="/" className="font-display text-3xl">TAXI/X</Link>
        <div>
          <p className="font-mono-eyebrow">// Rejoignez-nous</p>
          <h2 className="font-display text-5xl mt-3 leading-[0.95]">ROULEZ.<br/>CONDUISEZ.<br/>GAGNEZ.</h2>
        </div>
        <p className="font-mono-eyebrow">/ Inscription en 30 secondes</p>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <p className="font-mono-eyebrow">// Créer un compte</p>
          <h1 className="font-display text-4xl mt-2">INSCRIPTION</h1>

          <div className="mt-6 flex gap-2">
            {["passenger", "driver"].map(r => (
              <button
                key={r}
                type="button"
                data-testid={`register-role-${r}`}
                onClick={() => setForm(f => ({...f, role: r}))}
                className={`flex-1 border-2 border-black py-3 font-mono-eyebrow uppercase ${form.role === r ? 'bg-[#FFD600]' : 'bg-white'}`}
              >{ROLE_LABEL[r]}</button>
            ))}
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="font-mono-eyebrow">Nom</label>
              <input data-testid="register-name-input" required value={form.name} onChange={upd("name")} className="brut-input mt-2" />
            </div>
            <div>
              <label className="font-mono-eyebrow">E-mail</label>
              <input data-testid="register-email-input" type="email" required value={form.email} onChange={upd("email")} className="brut-input mt-2" />
            </div>
            <div>
              <label className="font-mono-eyebrow">Mot de passe</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="brut-input mt-2" />
            </div>
            <div>
              <label className="font-mono-eyebrow">Téléphone (optionnel)</label>
              <input data-testid="register-phone-input" value={form.phone} onChange={upd("phone")} className="brut-input mt-2" />
            </div>
            {form.role === "driver" && (
              <>
                <div>
                  <label className="font-mono-eyebrow">Type de véhicule</label>
                  <select data-testid="register-vehicle-type-select" value={form.vehicle_type} onChange={upd("vehicle_type")} className="brut-input mt-2">
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="xl">XL</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono-eyebrow">Plaque d'immatriculation</label>
                  <input data-testid="register-vehicle-plate-input" value={form.vehicle_plate} onChange={upd("vehicle_plate")} className="brut-input mt-2" placeholder="VD-12345" />
                </div>
              </>
            )}
            <button data-testid="register-submit-btn" disabled={loading} type="submit" className="brut-btn w-full">
              {loading ? "CRÉATION..." : "CRÉER LE COMPTE →"}
            </button>
            <p className="text-sm mt-4">Vous avez déjà un compte ? <Link data-testid="register-to-login-link" to="/login" className="font-bold underline">Se connecter</Link></p>
          </div>
        </form>
      </div>
    </div>
  );
}
